import classNames from 'classnames'
import React, { useCallback, useState } from 'react'

import { CategorySelectionTooltip } from '../categorySelectionInfo/CategorySelectionTooltip'

import { CategoryCheckbox } from './CategoryCheckbox'
import {
  expandChosenCategories,
  getInitialCategoriesExpanded,
  mapSubcategoryToCategory,
} from './helpers/expand'
import { TCategory, TSubcategory } from './models'
import { Subcategories } from './Subcategories'

import styles from './MultipleCategories.module.scss'

interface IProps {
  categories: TCategory[]
  chosenGUIDs: string[]
  onChoose: (item: TSubcategory) => void
  businessUnits: string[]
  isDisabled?: boolean
  isChosenExpanded?: boolean
}

export const MultipleCategories = ({
  categories,
  chosenGUIDs,
  onChoose,
  businessUnits,
  isDisabled,
  isChosenExpanded,
}: IProps) => {
  const [categoryExpanded, setCategoryExpanded] = useState(() => {
    const expanded = getInitialCategoriesExpanded(categories)
    if (isChosenExpanded) {
      const subcategoryToCategory = mapSubcategoryToCategory(categories)
      expandChosenCategories(expanded, subcategoryToCategory, chosenGUIDs)
    }

    return expanded
  })
  const checkIsChosen = useCallback(
    (categoryGUID: string) => chosenGUIDs.includes(categoryGUID),
    [chosenGUIDs]
  )

  return (
    <div className={styles.container}>
      {categories.map((category) => {
        const { subcategories, guidelines, description, justification } = category
        const guidelineTrigger = (
          <CategorySelectionTooltip
            guidelines={guidelines}
            description={description}
            justification={justification}
            mode={'multiple'}
          >
            <span className={styles.guidelinesTrigger}>View Guidelines</span>
          </CategorySelectionTooltip>
        )

        return (
          <div key={category.guid} className={classNames(styles.category, styles.categoryWrapper)}>
            {subcategories && subcategories.length ? (
              <div className={styles.categoryRow}>
                <div
                  className={styles.multipleCategoryTitle}
                  onClick={() =>
                    setCategoryExpanded((prevState) => {
                      return {
                        ...prevState,
                        [category.guid]: !prevState[category.guid],
                      }
                    })
                  }
                >
                  <div className={classNames(styles.expander, styles.expanderWrapper)}>
                    <i
                      className={
                        categoryExpanded[category.guid]
                          ? 'kbase kbase-caret-down'
                          : 'kbase kbase-caret-right'
                      }
                    />
                  </div>
                  {guidelineTrigger}
                  <span className={styles.categoryTitle}>{category.title}</span>
                </div>
              </div>
            ) : (
              <CategoryCheckbox
                key={category.guid}
                checked={checkIsChosen(category.guid)}
                label={
                  <span className={styles.categoryRowTitle}>
                    <span
                      className={classNames(styles.singleCategoryTitle, {
                        [styles.disabled]: isDisabled,
                      })}
                      onClick={() => !isDisabled && onChoose(category)}
                    >
                      {guidelineTrigger}
                      {category.title}
                    </span>
                  </span>
                }
                onChange={() => onChoose(category)}
                disabled={isDisabled}
              />
            )}

            {Boolean(subcategories.length) && Boolean(categoryExpanded[category.guid]) && (
              <Subcategories
                subcategories={subcategories}
                businessUnits={businessUnits}
                checkIsChosen={checkIsChosen}
                onChoose={onChoose}
                isDisabled={isDisabled}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}
